import { WidgetComponent } from '@juvare/widget-ui/WidgetComponent';
import { WidgetEditorContext } from '@juvare/widget-ui/WidgetEditorContext';
import { Box } from '@mui/material';
import React from 'react';
import { DragTargetPlaceholder } from '../../WidgetForm/Components';
import { getChildWidgetStyle, getVisibleError } from '../../WidgetForm/Utils';
import { DropTargetSpacerVertical } from './DropTargetSpacerVertical';
/**
 * Renders a flexbox-based row layout for arranging widgets in the editor or runtime.
 * Supports configurable spacing, alignment, and drag-and-drop placeholders in edit mode.
 *
 * @param {object} props - The component props.
 * @param {number} props.spacing - The space (in pixels) between child widgets.
 * @param {'left' | 'right' | 'center'} [props.horizontalAlign] - Horizontal alignment of children.
 *   - `left`: Aligns widgets to the start of the row.
 *   - `right`: Aligns widgets to the end of the row.
 *   - `center`: Centers widgets horizontally.
 * @param {'top' | 'bottom' | 'center'} [props.verticalAlign] - Vertical alignment of children.
 *   - `top`: Aligns widgets to the top of the row.
 *   - `bottom`: Aligns widgets to the bottom of the row.
 *   - `center`: Centers widgets vertically.
 * @param {string} props.childrenEditorPath - The editor path identifier for this layout's children.
 *   Used internally by the widget editor for positioning and drag-drop operations.
 * @param {WidgetProps<any, any>[]} props.childProps - An array of widget props objects for each child widget.
 *   When `editMode` is false, hidden widgets are filtered out.
 * @param {WidgetRenderData} props.renderData - Render-time data context for widgets.
 * @param {boolean} [props.editMode] - If `true`, the component is in edit mode and shows drop placeholders.
 * @param {string} [props.dragTargetPlaceholderMessage] - Custom message displayed in the drop target placeholder when empty in edit mode.
 * @param {SxProps<Theme>} [props.sx] - Additional MUI `sx` styling overrides applied to the row container.
 * @param {React.ReactNode} props.children - Child elements or components to be rendered inside the layout row.
 * @returns {JSX.Element} The rendered row layout container.
 */
export const BaseLayoutRow = (props) => {
    const editorContext = React.useContext(WidgetEditorContext);
    let childProps = props.childProps;
    if (!editorContext.editMode) {
        childProps = childProps.filter((p) => !p.hidden);
    }
    let justifyContent = undefined;
    switch (props.horizontalAlign) {
        case 'left':
            justifyContent = 'flex-start';
            break;
        case 'right':
            justifyContent = 'flex-end';
            break;
        case 'center':
            justifyContent = 'center';
            break;
    }
    let alignItems = undefined;
    switch (props.verticalAlign) {
        case 'top':
            alignItems = 'flex-start';
            break;
        case 'bottom':
            alignItems = 'flex-end';
            break;
        case 'center':
            alignItems = 'center';
            break;
    }
    return (React.createElement(Box, { sx: Object.assign(Object.assign({}, props.sx), { display: 'flex', justifyContent: justifyContent, alignItems: alignItems }), "data-editor-path": props.childrenEditorPath },
        childProps.length === 0 && (React.createElement("div", { style: { flexGrow: 1 } },
            React.createElement(DropTargetSpacerVertical, { spacing: props.spacing, first: true, last: true, editorPath: '#0+', placeholderElement: props.editMode ? (React.createElement(DragTargetPlaceholder, null, "Drag and drop components from the toolbar here")) : undefined }))),
        childProps.length > 0 &&
            childProps.map((childProp, index) => {
                return (React.createElement(React.Fragment, { key: childProp.id },
                    React.createElement(DropTargetSpacerVertical, { spacing: props.spacing, first: index === 0, last: false, editorPath: '#' + index + '+' }),
                    React.createElement(Box, { style: getChildWidgetStyle(childProp, { width: '100%' }), "data-editor-path": '#' + index, "data-widget": childProp.dataPath, "data-visible-error": getVisibleError(childProp) ? 'true' : undefined },
                        React.createElement(WidgetComponent, Object.assign({}, childProp, { renderData: props.renderData })))));
            }),
        childProps.length > 0 && (React.createElement(DropTargetSpacerVertical, { spacing: props.spacing, first: false, last: true, editorPath: '#' + childProps.length + '+' }))));
};
